import createClient, { type Middleware } from 'openapi-fetch';
import { toast } from 'sonner';
import type { paths } from '@worldathletics/api-client';

import { logout as logoutAction } from '@/context/store/slices/authSlice';
import { editorStore } from '@/context/store/store';

import { tryRefreshOnce } from './api-client-internal';
import { clearForceChangeContext } from './force-change-storage';
import { __setRefresher } from './silent-refresh';
import { tokenStorage } from './token-storage';

// Build-time constant injected by Vite via the `define` config in
// apps/web/vite.config.ts. In production/dev under Vite, this is statically
// replaced with the value of process.env.VITE_API_URL (or localhost fallback).
// Under Jest (no Vite), the constant is undefined and we use the fallback.
declare const __VITE_API_URL__: string | undefined;

const baseUrl: string =
  typeof __VITE_API_URL__ === 'string' && __VITE_API_URL__
    ? __VITE_API_URL__
    : 'http://localhost:3001';

// Public auth paths: no bearer token attached, no 401-refresh recursion, no auto-toast.
// Authenticated paths under /auth/* (e.g. change-password) MUST NOT appear in this list.
const PUBLIC_AUTH_PATHS = new Set<string>([
  '/auth/login',
  '/auth/refresh',
  '/auth/logout',
  '/auth/forgot-password',
  '/auth/confirm-forgot-password',
  '/auth/force-change-password',
  '/auth/register',
  '/auth/confirm-registration',
]);

// Paths that handle their own error UI inline; suppress the global toast.
const SKIP_AUTO_TOAST_PATHS = new Set<string>([
  '/auth/change-password',
  '/auth/forgot-password',
  '/auth/confirm-forgot-password',
  '/auth/force-change-password',
]);

const SUPPRESS_ERROR_TOAST_HEADER = 'x-suppress-error-toast';

function shouldSuppressErrorToast(request: Request): boolean {
  return request.headers.get(SUPPRESS_ERROR_TOAST_HEADER) === 'true';
}

function isPublicAuthPath(schemaPath: string): boolean {
  return PUBLIC_AUTH_PATHS.has(schemaPath);
}

function shouldSkipAutoToast(schemaPath: string): boolean {
  return SKIP_AUTO_TOAST_PATHS.has(schemaPath);
}

const authMiddleware: Middleware = {
  async onRequest({ request, schemaPath }) {
    if (isPublicAuthPath(schemaPath)) return request;
    const token = tokenStorage.getAccessToken();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    return request;
  },
};

const errorMiddleware: Middleware = {
  async onResponse({ request, response, schemaPath }) {
    // Public auth endpoints: never trigger refresh recursion or toast. Errors bubble up.
    if (isPublicAuthPath(schemaPath)) return response;

    if (response.status === 401) {
      const refreshed = await tryRefreshOnce();
      if (refreshed) {
        const newToken = tokenStorage.getAccessToken();
        const retried = new Request(request, {
          headers: new Headers(request.headers),
        });
        if (newToken) retried.headers.set('Authorization', `Bearer ${newToken}`);
        return fetch(retried);
      }
      // Refresh failed — force logout.
      tokenStorage.clearAll();
      clearForceChangeContext();
      editorStore.dispatch(logoutAction());
      window.location.assign('/login');
      return response;
    }

    // Auto-toast for transport-level errors, except on paths that handle inline.
    if (!shouldSkipAutoToast(schemaPath) && !shouldSuppressErrorToast(request)) {
      if (response.status === 403) {
        toast.error("You don't have permission to do that.");
      } else if (response.status >= 500) {
        toast.error('Something went wrong. Please try again.');
      } else if (response.status >= 400) {
        const body = (await response
          .clone()
          .json()
          .catch(() => null)) as { message?: string | string[] } | null;
        const msg = body?.message ?? `Request failed (${response.status})`;
        toast.error(Array.isArray(msg) ? msg.join(', ') : msg);
      }
    }
    return response;
  },
};

export const apiClient = createClient<paths>({ baseUrl });
// Order matters — auth attaches header on request, error reads response.
apiClient.use(authMiddleware);
apiClient.use(errorMiddleware);

// Wire silent-refresh to use tryRefreshOnce. Done at module load.
__setRefresher(tryRefreshOnce);
